import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import legacyApi from '@/services/instance';
import { httpV1, httpV2 } from '@/bundles/Http/factory/httpFactory';
import { MAX_VALUE_LENGTH, track } from '@/bundles/Analytics/helpers/gtm';
import { AnalyticsEventEnum, ApiDurationBucketEnum, ApiErrorTypeEnum } from '@/bundles/Analytics/types';

// a request that takes longer than this is reported as sc_api_slow
const SLOW_REQUEST_MS = 3000;

// Ids and values in a path (record keys, uuids, hashes, tokens, e-mails, encoded names) never
// leave the app, so /contact/123/roles is reported as /contact/:id/roles.
const VALUE_SEGMENT = /^\d+$|^[0-9a-f-]{16,}$|[@%\s]|^.{33,}$/i;

// When each request left the app. The clients' own interceptors mutate and pass on the same
// config object, so the response (or error) carries the key back.
const startedAt = new WeakMap<AxiosRequestConfig, number>();

export function endpointTemplate (url: string): string {
  const path = url.replace(/^[a-z]+:\/\/[^/]+/i, '').split(/[?#]/)[0];

  return path
    .split('/')
    .map((segment) => (VALUE_SEGMENT.test(segment) ? ':id' : segment))
    .join('/')
    .slice(0, MAX_VALUE_LENGTH);
}

function durationBucket (duration: number): ApiDurationBucketEnum {
  if (duration < 5000) return ApiDurationBucketEnum.UpTo5s;

  return duration < 10000 ? ApiDurationBucketEnum.UpTo10s : ApiDurationBucketEnum.Over10s;
}

function reportSlowRequest (config: AxiosRequestConfig, httpStatus: number): void {
  const start = startedAt.get(config);

  if (start === undefined) return;

  const duration = Math.round(performance.now() - start);

  if (duration < SLOW_REQUEST_MS) return;

  track(AnalyticsEventEnum.ApiSlow, {
    http_status: httpStatus,
    api_method: config.method?.toUpperCase(),
    api_endpoint: config.url ? endpointTemplate(config.url) : undefined,
    api_duration_ms: duration,
    duration_bucket: durationBucket(duration),
  });
}

function reportApiError (error: unknown): void {
  // Only requests the API failed or never answered. A cancelled request (the clients rethrow it as
  // a plain Error) and anything the app's own interceptors throw are not API failures. The legacy
  // refresh helper also rethrows a services/instance request that got no answer as a TypeError,
  // so that client's network failures are skipped too.
  if (!axios.isAxiosError(error)) return;

  const { config, response } = error;

  track(AnalyticsEventEnum.ApiError, {
    error_type: response ? ApiErrorTypeEnum.Http : ApiErrorTypeEnum.Network,
    http_status: response?.status ?? 0,
    api_method: config.method?.toUpperCase(),
    api_endpoint: config.url ? endpointTemplate(config.url) : undefined,
  });
  reportSlowRequest(config, response?.status ?? 0);
}

// The request interceptor runs before each client's own ones (axios runs them last-in first-out)
// and the response one after them, so a 401 that a token refresh recovers is never reported as
// an error. Reporting is best effort: responses and errors always pass on untouched.
export function trackApiRequests (): void {
  const clients: AxiosInstance[] = [legacyApi, httpV1.client.instance, httpV2.client.instance];

  clients.forEach((client) => {
    client.interceptors.request.use((config) => {
      startedAt.set(config, performance.now());

      return config;
    });
    client.interceptors.response.use((response) => {
      try {
        reportSlowRequest(response.config, response.status);
      } catch {
        // reporting is best effort
      }

      return response;
    }, (error) => {
      try {
        reportApiError(error);
      } catch {
        // reporting is best effort
      }

      return Promise.reject(error);
    });
  });
}
